import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import { useAuthStore } from "@/store/authStore";


const api: AxiosInstance = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 60000,
  withCredentials: true,
});

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { user, accessToken } = useAuthStore.getState();

    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    if (user?.id) {
      config.headers["x-user-id"] = String(user.id);
    }
    if (user?.role != null) {
      config.headers["x-user-role"] = String(user.role);
    }

    const url = (config.url ?? "") as string;
    if (url.startsWith("/auth/")) {
      config.timeout = 60000;
    }
    // In dev, first request to a route can trigger Next.js compilation (30s+). Use longer timeout so booking confirm succeeds.
    if (url === "/bookings" && (config.method === "post" || config.method === "POST")) {
      config.timeout = 90000;
    }

    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

function getNetworkErrorMessage(error: AxiosError): string | null {
  if (error.code === "ECONNABORTED" || error.message?.toLowerCase().includes("timeout")) {
    return "The request took too long. Please check your connection and try again.";
  }
  if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
    return "Unable to connect. Please check your internet connection and try again.";
  }
  if (error.code === "ECONNREFUSED" || error.code === "ENOTFOUND") {
    return "Unable to reach the server. Please try again later.";
  }
  return null;
}

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const requestUrl = (error.config?.url ?? "") as string;

    if (status === 401) {
      const isLoginRequest = requestUrl.startsWith("/auth/login");
      const isRefreshRequest = requestUrl.startsWith("/auth/refresh");
      const isMeRequest = requestUrl.startsWith("/auth/me");
      if (!isLoginRequest && !isRefreshRequest && !isMeRequest && error.config) {
        const refreshed = await useAuthStore.getState().refreshAccessToken();
        if (refreshed) {
          const newToken = useAuthStore.getState().accessToken;
          if (newToken) {
            (error.config as InternalAxiosRequestConfig).headers["Authorization"] = `Bearer ${newToken}`;
            return api.request(error.config as InternalAxiosRequestConfig);
          }
        }
        useAuthStore.getState().setUser(null);
        useAuthStore.getState().setAccessToken(null);
        if (typeof window !== "undefined" && window.location.pathname !== "/login") {
          const pathname = window.location.pathname;
          const isBookingRoute = pathname.startsWith("/booking/");
          if (!isBookingRoute) {
            window.location.href = `/login?returnUrl=${encodeURIComponent(pathname)}`;
          }
        }
      }
      return Promise.reject(error);
    }

    const friendlyMessage = getNetworkErrorMessage(error);
    if (friendlyMessage) {
      error.message = friendlyMessage;
      const response = error.response ?? ({} as AxiosResponse);
      (response as { data?: Record<string, unknown> }).data = {
        ...(response.data as object),
        error: friendlyMessage,
        code: "NETWORK_ERROR",
      };
      (error as { response: AxiosResponse }).response = response;
    }
    return Promise.reject(error);
  },
);

export default api;